/* @flow */
import * as React from 'react';
import { graphql, compose } from 'react-apollo';
import idx from 'idx';

import ErrorActions from 'actions/ErrorActions';
import ToastActions from 'actions/ToastActions';
import LinkConstants from 'constants/LinkConstants';
import Group from 'entities/Group';
import GroupActions from 'groups/actions/GroupActions';
import GET_CLASS_PROFILE_QUERY from 'groups/queries/ClassProfileQuery.gql';
import GET_BASIC_CLASS_PROFILE_QUERY from 'groups/queries/BasicClassProfileQuery.gql';

import GroupProfile from './GroupProfile';

import type { GroupProfileType } from 'groups/components/GroupProfile';
import type { ClassProfile } from 'groups/queries/__generated__/ClassProfile';
import type { UserProfile } from 'users/components/UsersProfile';
import type { errorFragment } from 'queries/__generated__/errorFragment';

type Props = {
  error: errorFragment,
  group: GroupProfileType,
  loading: boolean,
  onBackClicked?: () => void,
  onClose: () => void,
  onRemoveGroupFromOrg: ?() => void,
  onSubscribe: () => void,
  onUnsubscribe: () => void,
  onUserChanged: () => void,
  onUserClicked: (user: UserProfile, role: string) => void,
  onViewAllAnnouncementsClicked: (groupUuid: string) => void,
  openModalRemoveGroupFromOrgEventKey: string,
  refetch: () => Promise<ClassProfile>,
  reloadQuery: boolean,
  isAdmin: boolean,
};

class GroupProfileContainer extends React.Component<Props> {
  componentDidUpdate(prevProps: Props) {
    const { reloadQuery, refetch, error, loading, group, onBackClicked, onClose } = this.props;

    if (reloadQuery && !prevProps.reloadQuery) {
      refetch();
    }

    if (error) {
      ErrorActions.serverError(error);
    }

    if (!loading && !group) {
      ToastActions.showErrorToast(
        `
          There was an error. Please try again, or email
          ${LinkConstants.SUPPORT_EMAIL}
          for help.
        `
      );

      if (onBackClicked) {
        onBackClicked();
      } else {
        onClose();
      }
    }
  }

  subscribeToGroup = () => {
    const { group } = this.props;

    if (!group) {
      return;
    }

    GroupActions.subscribeToGroup(((Group.fromJSON(group): any): Group)).then(
      () => {
        ToastActions.showSuccessToast(`You’ve joined ${group.className}`);
        this.props.refetch();
      },
      error => {
        const errorMessage = error.message || 'Error joining class';
        ToastActions.showErrorToast(errorMessage);
        ErrorActions.serverError(error);
      }
    );
  };

  unsubscribeToGroup = () => {
    const { group } = this.props;

    if (!group) {
      return;
    }

    GroupActions.unsubscribeFromGroup(((Group.fromJSON(group): any): Group)).then(
      () => {
        ToastActions.showSuccessToast(`You’ve left ${group.className}`);
        this.props.refetch();
      },
      error => {
        const errorMessage = error.message || 'Error leaving class';
        ToastActions.showErrorToast(errorMessage);
        ErrorActions.serverError(error);
      }
    );
  };

  render() {
    const {
      group,
      loading,
      onBackClicked,
      onClose,
      onRemoveGroupFromOrg,
      onUserChanged,
      onUserClicked,
      onViewAllAnnouncementsClicked,
      openModalRemoveGroupFromOrgEventKey,
    } = this.props;

    return (
      <GroupProfile
        group={group}
        loading={loading}
        onBackClicked={onBackClicked}
        onClose={onClose}
        onRemoveGroupFromOrg={onRemoveGroupFromOrg}
        onSubscribe={this.subscribeToGroup}
        onUnsubscribe={this.unsubscribeToGroup}
        onUserChanged={onUserChanged}
        onUserClicked={onUserClicked}
        onViewAllAnnouncementsClicked={onViewAllAnnouncementsClicked}
        openModalRemoveGroupFromOrgEventKey={openModalRemoveGroupFromOrgEventKey}
      />
    );
  }
}

const withClassProfile = graphql(GET_CLASS_PROFILE_QUERY, {
  options: ({ group }) => {
    return {
      variables: {
        classUuid: group.uuid,
      },
      fetchPolicy: 'network-only',
    };
  },
  props: ({ data, ownProps }) => ({
    loading: data.loading,
    error: data.error,
    // fallback to partial loaded group
    group: data.loading ? ownProps.group : idx(data.classes, _ => _[0]) || ownProps.group,
    refetch: data.refetch,
  }),
  skip: ({ group, isAdmin }) => !group.isMember && !isAdmin,
});

const withBasicClassProfile = graphql(GET_BASIC_CLASS_PROFILE_QUERY, {
  options: ({ group }) => {
    return {
      variables: {
        classUuid: group.uuid,
      },
      fetchPolicy: 'network-only',
    };
  },
  props: ({ data, ownProps }) => ({
    loading: data.loading,
    error: data.error,
    // fallback to partial loaded group
    group: data.loading ? ownProps.group : idx(data.classes, _ => _[0]) || ownProps.group,
    refetch: data.refetch,
  }),
  skip: ({ group, isAdmin }) => isAdmin || group.isMember,
});

export { GroupProfileContainer };

export default compose(
  withClassProfile,
  withBasicClassProfile
)(GroupProfileContainer);
